import { Checkbox, Field, Input, makeStyles, tokens, Text, RadioGroup, Radio, Dropdown, Spinner, RadioGroupOnChangeData, mergeClasses, Option } from '@fluentui/react-components';
import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate, useParams } from 'react-router-dom';

import { userContextSlice } from '@tdp/common/auth';
import { AppDetailsHeader } from '@tdp/common/components/AppDetailsHeader';
import { LoadingStates } from '@tdp/common/constants';
import { makeDocsLink } from '@tdp/common/constants';
import { InputSection } from '@tdp/common/detailsPage';
import { Flex, StyledText } from '@tdp/common/fluentV9MigrationComponents';
import { useCommand, useSelector } from '@tdp/core';
import { botSlice } from '@tdp/modules/bots';

import { AppFeaturesMEDomain } from './AppFeaturesMEDomain';
import { createAppZoomHook } from '../../../hooks';
import { GroupChoiceKey, onActivate } from '../AppFeaturesCommon/AppBotSelectUtils';
import { appFeaturesCommonStyles } from '../AppFeaturesCommon/appFeaturesCommon.styles';
import { AppFeaturesEmpty } from '../AppFeaturesCommon/AppFeaturesEmpty';
import { createAppFeaturesFooter } from '../AppFeaturesCommon/AppFeaturesFooter';

const useStyles = makeStyles({
  radioFullWidth: {
    '& .fui-Radio__label': {
      width: '100%',
    },
  },
  spaced: {
    rowGap: '20px',
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    '& .fui-Radio': {
      width: '100%',
    },
  },
  section: {
    padding: tokens.spacingHorizontalL,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingHorizontalM,
    maxWidth: '700px',
  },
  flexColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingHorizontalS,
  },
  button: {
    marginTop: tokens.spacingVerticalXL,
    maxWidth: '300px',
    justifyContent: 'flex-start',
  },
  dropdown: {
    marginTop: '5px',
    marginBottom: '10px',
    marginLeft: '0',
    marginRight: '0',
  },
  input: {
    marginTop: '5px',
    marginLeft: '25px',
    marginBottom: '0',
    marginRight: '0',
  },
  inlineLink: {
    width: 'fit-content',
    marginTop: '0',
    marginRight: '0',
  },
  link: {
    cursor: 'pointer',
    color: tokens.colorBrandForegroundLink, // Fluent UI token for link color
    textDecoration: 'none',
    ':hover': {
      textDecoration: 'underline',
      color: tokens.colorBrandForegroundLinkHover, // Fluent UI token for link hover color
    },
  },
  errorText: {
    color: tokens.colorPaletteRedForeground1,
    marginTop: tokens.spacingVerticalXXS,
  },
});

const useMe = createAppZoomHook(app => app.messagingExtensions);
const Footer = createAppFeaturesFooter(app => [app.messagingExtensions]);

export const AppFeaturesME: React.FC = () => {
  const navigate = useNavigate();
  const baseStyles = appFeaturesCommonStyles();
  const classes = useStyles();
  const intl = useIntl();
  const appId = useParams<{ appId: string }>().appId ?? '';
  const messageExt = useMe(appId);
  const currentMessageExt = messageExt.current?.[0];
  const currentMessageHandlers = messageExt.current?.[0]?.messageHandlers?.[0];
  const botList = useSelector(botSlice.selectors.selectAll);
  const botListFetchStatus = useSelector(botSlice.selectors.botListApiStatus);
  const loadBots = useCommand((commands) => commands.botCommands.loadBots);

  const [selectedBot, setSelectedBot] = useState<string>('');
  const isMicrosoftTenant = !!useSelector(
    userContextSlice.selectors.isMicrosoftTenant
  );
  const [currentRadioGroup, setCurrentRadioGroup] = useState<GroupChoiceKey>(
    GroupChoiceKey.SelectFromExistingBots
  );

  const options = React.useMemo(
    () =>
      botList.map((bot) => ({
        key: bot.botId,
        text: `${bot.name} (${bot.botId})`,
      })),
    [botList]
  );

  useEffect(() => {
    if (botListFetchStatus === LoadingStates.NOT_STARTED) {
      loadBots();
    }
  }, []);


  useEffect(() => {
    if (botListFetchStatus === LoadingStates.SUCCESS && messageExt.current?.[0]) {
      const selectedBotList = botList.filter(
        (bot) => bot.botId === messageExt.current?.[0].botId
      );
      if (selectedBotList.length > 0) {
        setSelectedBot(messageExt.current[0].botId ?? "");
        setCurrentRadioGroup(GroupChoiceKey.SelectFromExistingBots);
      } else {
        setCurrentRadioGroup(GroupChoiceKey.ConnectToDifferentBot);
      }
    }
  }, [botListFetchStatus, messageExt.base]);


  const onBotIdChanged = (val: string) => {
    messageExt.update(t => [
      { ...currentMessageExt, botId: val, }
    ])
  }

  //canUpdateConfiguration
  //supportsAnonymousAccess

  const onDomainUpdated = (val: string[]) => {
    messageExt.update(t => [{
      ...currentMessageExt,
      messageHandlers: [{
        ...currentMessageHandlers,
        value: {
          ...currentMessageHandlers?.value,
          domains: val
        }
      }]
    }])
  }

  const handleRevert = React.useCallback(() => {
    const selectedBotList = botList.filter(
      (bot) => bot.botId === messageExt.base?.[0].botId
    );
    if (selectedBotList.length > 0) {
      setSelectedBot(messageExt.base?.[0]?.botId ?? "");
      setCurrentRadioGroup(GroupChoiceKey.SelectFromExistingBots);
    } else {
      setCurrentRadioGroup(GroupChoiceKey.ConnectToDifferentBot);
    }
  }, [botList, messageExt.base, setSelectedBot, setCurrentRadioGroup]);

  const onCreateBotHandler = (
    e: React.MouseEvent | React.KeyboardEvent
  ): void => {
    e.stopPropagation();
    navigate(`/tools/bots`);
  };


  const isGuidError = () => {
    return !(currentMessageExt?.botId
      ? /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
        currentMessageExt.botId
      )
      : true);
  };

  // Event Handlers
  const handleBotGroupChange = (
    ev: React.FormEvent<HTMLDivElement>,
    data: RadioGroupOnChangeData
  ) => {
    setCurrentRadioGroup(data.value as GroupChoiceKey);
  };

  const selectFromExistingBots = intl.formatMessage({
    defaultMessage: 'Select an existing bot',
    description: 'Label for selecting from existing bots',
  });

  const connectToDifferentBot = intl.formatMessage({
    defaultMessage: 'Enter a bot ID',
    description: 'Label for connecting to a different bot',
  });

  return (
    <div className={baseStyles.wrapper}>
      <div className={baseStyles.container}>
        <AppDetailsHeader
          onBackNavigation={() => { navigate(-1); }}
          showBackNavigation
          title={<FormattedMessage
            defaultMessage="Messaging extension"
            description={'Title for Messaging extension'}
          />}
          description={<FormattedMessage
            defaultMessage="A shortcut for inserting your app’s content or acting on a message without navigating away from a conversation."
            description={'Description for Messaging extension'}
          />}
          link={<FormattedMessage
            defaultMessage="Learn more about Bots"
            description={'Link to learn more about Messaging extension'}
          />}
          href={makeDocsLink("microsoftteams/platform/concepts/messaging-extensions")}
        />
        <br />
        <section className={classes.section}>
          <InputSection
            legend={<FormattedMessage defaultMessage="Identify your bot" />}
          >
            <Field id="botId">
              <RadioGroup
                layout="vertical"
                value={currentRadioGroup}
                onChange={handleBotGroupChange}
                className={classes.spaced}
              >
                <Radio
                  key={GroupChoiceKey.SelectFromExistingBots}
                  value={GroupChoiceKey.SelectFromExistingBots}
                  className={classes.radioFullWidth}
                  label={
                    <Flex column>
                      {selectFromExistingBots}
                      <Dropdown
                        className={classes.dropdown}
                        disabled={
                          currentRadioGroup !==
                          GroupChoiceKey.SelectFromExistingBots
                        }
                        aria-required={true}
                        aria-labelledby={'botId'}
                        selectedOptions={[selectedBot]}
                        onOptionSelect={(_, data) => {
                          setSelectedBot(data.optionValue ?? '');
                          onBotIdChanged(data.optionValue ?? '');
                        }}
                        value={selectedBot}
                        placeholder={botListFetchStatus === LoadingStates.LOADING
                          ? intl.formatMessage({ defaultMessage: 'Loading ...' })
                          : selectFromExistingBots}
                      >
                        {botListFetchStatus === LoadingStates.LOADING ? (
                          <Option value="loading" disabled>
                            <Spinner
                              size="tiny"
                              label={intl.formatMessage({
                                defaultMessage: 'Loading ...',
                              })}
                            />
                          </Option>
                        ) : options.length === 0 ? (
                          <Option value="no-bots" disabled>
                            {
                              <>
                                <StyledText>
                                  {intl.formatMessage({
                                    defaultMessage: 'No bots found.',
                                    description: 'No bots found declaration',
                                  })}
                                </StyledText>{' '}
                                {!isMicrosoftTenant && (
                                  <Text
                                    className={classes.link}
                                    {...onActivate(onCreateBotHandler)}
                                  >
                                    {intl.formatMessage({
                                      description: 'create new bot label',
                                      defaultMessage: 'Create a new bot',
                                    })}
                                  </Text>
                                )}
                              </>
                            }
                          </Option>
                        ) : (
                          options.map((option) => (
                            <Option key={option.key} value={option.key}>
                              {option.text}
                            </Option>
                          ))
                        )}
                      </Dropdown>
                      {!isMicrosoftTenant && (
                        <Text
                          className={mergeClasses(
                            classes.inlineLink,
                            classes.link
                          )}
                          {...onActivate(onCreateBotHandler)}
                        >
                          {intl.formatMessage({
                            defaultMessage: 'Create a new bot',
                            description:
                              'Create a new bot using management tool',
                          })}
                        </Text>
                      )}
                    </Flex>
                  }
                />

                <Radio
                  key={GroupChoiceKey.ConnectToDifferentBot}
                  value={GroupChoiceKey.ConnectToDifferentBot}
                  className={classes.radioFullWidth}
                  label={
                    <Flex column>
                      {connectToDifferentBot}
                      <Input
                        disabled={
                          currentRadioGroup !==
                          GroupChoiceKey.ConnectToDifferentBot
                        }
                        type="text"
                        placeholder={'XXXXXXXX-XXXX-XXXX-XXXX-XXXXXXXXXXXX'}
                        value={currentMessageExt?.botId ?? ''}
                        onChange={(_, data) => {
                          onBotIdChanged(data.value);
                        }}
                      />
                      {isGuidError() && (
                        <div className={classes.errorText}>
                          <FormattedMessage
                            defaultMessage="Bot Id must be a valid GUID"
                            description={'Error message for invalid bot id'}
                          />
                        </div>
                      )}
                    </Flex>
                  }
                />
              </RadioGroup>
            </Field>
          </InputSection>
        </section>
        <section className={classes.section}>
          <div className={classes.flexColumn}>
            <Field label={intl.formatMessage({ defaultMessage: "Select to give users the ability to modify message extension settings" })} />
            <Checkbox
              label={intl.formatMessage({ defaultMessage: "Users can reconfigure the app" })}
              checked={currentMessageExt?.canUpdateConfiguration ?? false}
              onChange={(e, data) => { messageExt.update(t => [{ ...currentMessageExt, canUpdateConfiguration: !!data.checked }]) }}
            />
          </div>
        </section>
        <section className={classes.section}>
          <Text weight='bold'>
            <FormattedMessage
              defaultMessage="Link unfurling"
              description='Description for Link unfurling'
            />
          </Text>
          <Text>
            <FormattedMessage
              defaultMessage="Allows users to insert content-rich links (such as an Adaptive Card) from a domain you identify into a message."
              description='Description for Link unfurling'
            />
          </Text>
          <div className={classes.flexColumn}>
            <Field label={intl.formatMessage({ defaultMessage: "Select to indicate that the app can support invoke payloads from which user information has been redacted." })} />
            <Checkbox
              label={intl.formatMessage({ defaultMessage: "Supports anonymized payloads" })}
              checked={currentMessageExt?.messageHandlers?.[0]?.value?.supportsAnonymousAccess ?? false}
              onChange={(e, data) => {
                messageExt.update(t => [{
                  ...currentMessageExt,
                  messageHandlers: [{
                    ...currentMessageHandlers,
                    value: {
                      ...currentMessageHandlers?.value,
                      supportsAnonymousAccess: !!data.checked
                    }
                  }]
                }])
              }}
            />
          </div>
        </section>
        <section className={classes.section}>
          <AppFeaturesMEDomain
            items={[...(currentMessageHandlers?.value?.domains ?? [])]}
            onUpdate={onDomainUpdated}
          />
        </section>
      </div>
      <Footer isError={!(currentMessageExt?.botId) || isGuidError()} onRevertCallback={handleRevert} />
    </div >
  )
};
